import { Navigate, Outlet, useRoutes, useParams } from 'react-router';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import MainLayout from '../layouts/main';
import RentersLayout from '../layouts/RentersLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import NewRenterExperienceGuard from '../guards/NewRenterExperienceGuard';
import RenterResourceGuard from '../guards/RenterResourceGuard';
import UserAdministratorGuard from '../guards/UserAdministratorGuard';
// components
import { DASHBOARD_GROUPS } from '../utils/dashboard';
import ComponentLoader from 'src/routes/ComponentLoader';
import NotificationsPage from '../pages/Notifications';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import RenterHomePage from 'src/components/NewRenterInterface/Home/RenterHomePage';
import CertificationHubPage from 'src/components/NewRenterInterface/CertificationHub/CertificationHubPage';
// FileDetail is imported directly (not lazy) so it shares UploadDocumentsPage's
// chunk and avoids a full-screen spinner on first navigation to a file detail.
import AIFileUploadV2FileDetail from '../components/NewRenterInterface/AIFileUploadV2/FileDetail';
// InsightsReview is imported directly (not lazy) so navigating from the
// upload page to the review page does not trigger a full-screen LoadingScreen
// while the chunk downloads.
import AIFileUploadV2InsightsReview from '../components/NewRenterInterface/AIFileUploadV2/InsightsReview';
import RenterQuestionnairePage from 'src/components/NewRenterInterface/Home/QuestionnairePage/RenterQuestionnairePage';
import DataVaultTransferPage from 'src/pages/tenant/DataVaultTransfer/DataVaultTransferPage';
import RenterSignaturePage from 'src/components/NewRenterInterface/Signatures/RenterSignaturePage';
import SigningViewPage from 'src/components/NewRenterInterface/Signatures/SigningViewPage';
import RenterHistoryPage from 'src/components/NewRenterInterface/RenterHistoryPage';
import NewRenterNotificationsPage from 'src/components/NewRenterInterface/RenterNotificationsPage';
import RenterAppointmentsPage from 'src/components/NewRenterInterface/Appointments/RenterAppointmentsPage';
import RenterBookingFlow from 'src/components/NewRenterInterface/Appointments/RenterBookingFlow';
import AccountHubRouter from 'src/components/NewRenterInterface/AccountHub/AccountHubRouter';
import AccountHubPage from 'src/components/NewRenterInterface/AccountHub/AccountHubPage';
import AccountHubLayout from 'src/components/NewRenterInterface/AccountHub/AccountHubLayout';
import AIFileUploadV2Layout from 'src/components/NewRenterInterface/AIFileUploadV2/layout/AIFileUploadV2Layout';
import LoginSecurityPage from 'src/components/NewRenterInterface/AccountHub/LoginSecurityPage';
import ContactInfoPage from 'src/components/NewRenterInterface/AccountHub/ContactInfoPage';
import PersonalInfoPage from 'src/components/NewRenterInterface/AccountHub/PersonalInfoPage';
import DataPrivacyPage from 'src/components/NewRenterInterface/AccountHub/DataPrivacyPage';
import PropertyListPage from 'src/components/NewRenterInterface/AccountHub/PropertyListPage';
import TFAPage from 'src/components/NewRenterInterface/AccountHub/TFAPage';
import TFASetupPage from 'src/components/NewRenterInterface/AccountHub/TFASetupPage';
import ManageAuthenticatorPage from 'src/components/NewRenterInterface/AccountHub/ManageAuthenticatorPage';
import BackupCodesPage from 'src/components/NewRenterInterface/AccountHub/BackupCodesPage';
import SessionsPage from 'src/components/NewRenterInterface/AccountHub/SessionsPage';
import { DataVaultLayout } from 'src/layouts/data-vault/DataVaultLayout';
import { SurveyLayout } from 'src/layouts/survey/SurveyLayout';
import { SurveyIndexPage } from 'src/components/NewRenterInterface/Survey/SurveyIndexPage';
import InterviewRoute from 'src/pages/Interview/InterviewRoute';
import SurveyInterviewRoute from 'src/pages/Interview/SurveyInterviewRoute';
import InterviewCompletePage from 'src/pages/Interview/InterviewCompletePage';
import InterviewReAnswerRoute from 'src/pages/Interview/InterviewReAnswerRoute';
import MyDocumentsPage from 'src/components/NewRenterInterface/Survey/MyDocumentsPage';
import MyDocumentsInterviewRoute from 'src/pages/Interview/MyDocumentsInterviewRoute';
import { DataVaultLandingPage } from 'src/components/NewRenterInterface/DataVault/DataVaultLandingPage';
import { PQIndexPage } from 'src/components/NewRenterInterface/DataVault/PQIndexPage';
import { PQSectionDetailPage } from 'src/components/NewRenterInterface/DataVault/PQSectionDetailPage';
import DataVaultInterviewRoute from 'src/pages/Interview/DataVaultInterviewRoute';
import { HistoryHubPage } from 'src/components/NewRenterInterface/DataVault/History/HistoryHubPage';
import { CertificationSnapshotDetailPage } from 'src/components/NewRenterInterface/DataVault/History/CertificationSnapshotDetailPage';
import { CertificationSnapshotSectionDetailPage } from 'src/components/NewRenterInterface/DataVault/History/CertificationSnapshotSectionDetailPage';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'reset-password', element: <ResetPassword /> },
        {
          path: 'reset-password/:uidb64/:token',
          element: <ResetPasswordConfirmation />,
        },
        {
          path: 'create-password/:uidb64/:token',
          element: <ResetPasswordConfirmation createPassword />,
        },
        {
          path: 'impersonate-user/',
          element: <ActAsUser />,
        },
        // ENG-3357 / ENG-3554: federated (OIDC) sign-in callback landing. The
        // backend redirects here with a single-use code which the page
        // exchanges for JWTs. Must stay outside GuestGuard (the user is not yet
        // signed in). `/auth/google/complete` is kept as an alias for in-flight
        // sessions during the deploy.
        { path: 'federated/complete', element: <GoogleCompletePage /> },
        { path: 'google/complete', element: <GoogleCompletePage /> },
        { path: ':auth_link_code', element: <OneClickLogin /> },
      ],
    },

    { path: 'verify_email_address/:auth_email_code', element: <VerifyEmail /> },

    // ENG-2332: ULCM OTP magic-link verification (public, no auth required)
    { path: 'vo', element: <UlcmVerifyOtpPage /> },
    { path: 'ulcm/verify-otp', element: <UlcmVerifyOtpPage /> },

    // ENG-2324: ULCM cancellation link (public, no auth required)
    { path: 'ulcm/cancel/:token', element: <UlcmCancelByTokenPage /> },

    // Account Recovery — renter self-serve unlock + opt-back-in
    { path: 'account-recovery', element: <AccountRecoveryPage /> },

    // ENG-4253 — paper-mail account claim (public, no auth required)
    { path: 'claim', element: <ClaimPage /> },

    // ENG-3351 — profile-only self-registration (no property / cert-config).
    {
      path: 'get-started',
      element: <SelfRegistrationLayout />,
      children: [{ index: true, element: <GetStartedPage /> }],
    },

    // Self-registration v2 (ENG-1926)
    {
      path: 'registration',
      element: <SelfRegistrationLayout />,
      children: [
        { index: true, element: <SelfRegistrationListPage /> },
        {
          path: ':configUuid/success',
          element: <SelfRegistrationSuccessPage />,
        },
        {
          path: ':configUuid',
          element: <SelfRegistrationPage />,
        },
      ],
    },

    // Household Members Routes
    {
      path: 'user',
      element: (
        <RoleBasedGuard accessibleRoles={['resident']}>
          <DashboardLayout />
        </RoleBasedGuard>
      ),
      children: [
        {
          children: [{ path: '', element: <TenantLandingWrapper /> }],
        },
        {
          path: 'add-invite',
          children: [
            {
              path: 'add-household-member',
              element: <InviteToHouseholdHolder />,
            },
          ],
        },
        {
          path: 'signatures',
          children: [
            { path: '', element: <Signatures /> },
            { path: ':form_id', element: <SignaturesDetail /> },
          ],
        },
        {
          path: 'questionnaire-completed',
          children: [
            { path: ':survey_id', element: <QuestionnaireCompleted /> },
          ],
        },
        {
          path: 'settings',
          children: [{ path: '', element: <RenterSettingsPage /> }],
        },
        {
          path: 'notifications',
          children: [{ path: '', element: <RenterNotificationsPage /> }],
        },
      ],
    },

    // New Renter Experience Routes
    {
      path: RENTER_PATHS.HOME,
      element: (
        <RoleBasedGuard accessibleRoles={['resident']}>
          <NewRenterExperienceGuard>
            <RentersLayout />
          </NewRenterExperienceGuard>
        </RoleBasedGuard>
      ),
      children: [
        { path: '', element: <RenterHomePage /> },
        {
          path: RENTER_PATHS.CERTIFICATION,
          element: (
            <RenterResourceGuard>
              <Outlet />
            </RenterResourceGuard>
          ),
          children: [
            {
              path: 'survey/:surveyId',
              children: [
                {
                  path: 'questionnaire',
                  element: <RenterQuestionnairePage />,
                },
                {
                  path: 'data-transfer',
                  element: <DataVaultTransferPage />,
                },
              ],
            },
            // /renter/certification/:certificationId/files/:userId
            {
              path: RENTER_PATHS.CERT_HUB,
              element: <AIFileUploadV2CertEntry />,
            },
            {
              path: RENTER_PATHS.CERT_HUB_CLASSIFY,
              element: <AIFileUploadV2CertClassify />,
            },
            {
              path: RENTER_PATHS.CERT_HUB_REVIEW,
              element: <AIFileUploadV2CertReview />,
            },
            {
              path: RENTER_PATHS.CERT_HUB_DETAIL,
              element: <AIFileUploadV2CertFileDetail />,
            },
            // /renter/certification/:certificationId/hub/:userId (ENG-2968)
            {
              path: RENTER_PATHS.CERT_HUB_PAGE,
              element: <CertificationHubPage />,
            },
          ],
        },
        {
          path: RENTER_PATHS.SIGNATURES,
          children: [
            { path: '', element: <RenterSignaturePage /> },
            {
              path: 'sign/:fsrId',
              element: <SigningViewPage />,
            },
            {
              path: 'view/:certId/doc/:docId',
              element: <SigningViewPage />,
            },
          ],
        },
        { path: RENTER_PATHS.HISTORY, element: <RenterHistoryPage /> },
        // ENG-3895 — renter self-booking
        {
          path: RENTER_PATHS.APPOINTMENTS,
          element: <RenterAppointmentsPage />,
        },
        {
          path: RENTER_PATHS.APPOINTMENTS_BOOK,
          element: <RenterBookingFlow />,
        },
        {
          path: RENTER_PATHS.NOTIFICATIONS,
          element: <NewRenterNotificationsPage />,
        },
        {
          path: RENTER_PATHS.SETTINGS,
          element: (
            <Navigate
              to={`/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`}
              replace
            />
          ),
        },
        {
          path: RENTER_PATHS.ACCOUNT_HUB,
          element: <AccountHubRouter />,
          children: [
            { path: '', element: <AccountHubLayout /> },
            {
              path: RENTER_PATHS.ACCOUNT_HUB_USER,
              element: <AccountHubLayout />,
              children: [
                { path: '', element: <AccountHubPage /> },
                {
                  path: RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY,
                  element: <LoginSecurityPage />,
                },
                {
                  path: `${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}`,
                  element: <TFAPage />,
                },
                {
                  path: `${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA_SETUP}`,
                  element: <TFASetupPage />,
                },
                {
                  path: `${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA_MANAGE}`,
                  element: <ManageAuthenticatorPage />,
                },
                {
                  path: `${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_BACKUP_CODES}`,
                  element: <BackupCodesPage />,
                },
                {
                  path: `${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_SESSIONS}`,
                  element: <SessionsPage />,
                },
                {
                  path: RENTER_PATHS.ACCOUNT_HUB_CONTACT_INFO,
                  element: <ContactInfoPage />,
                },
                {
                  path: RENTER_PATHS.ACCOUNT_HUB_PERSONAL_INFO,
                  element: <PersonalInfoPage />,
                },
                {
                  path: RENTER_PATHS.ACCOUNT_HUB_DATA_PRIVACY,
                  element: <DataPrivacyPage />,
                },
                {
                  path: RENTER_PATHS.ACCOUNT_HUB_PROPERTY_LIST,
                  element: <PropertyListPage />,
                },
              ],
            },
          ],
        },
        // Data Vault routes (doc 04)
        {
          element: <DataVaultLayout />,
          children: [
            { path: 'data-vault', element: <DataVaultLandingPage /> },
            { path: 'data-vault/pq', element: <PQIndexPage /> },
            {
              path: 'data-vault/pq/sections/:sectionId',
              element: <PQSectionDetailPage />,
            },
            {
              path: 'data-vault/pq/answer',
              element: <DataVaultInterviewRoute />,
            },
            {
              path: 'data-vault/pq/sections/:sectionId/answer',
              element: <DataVaultInterviewRoute />,
            },
            {
              path: 'data-vault/members/:memberId',
              element: <DataVaultLandingPage />,
            },
            {
              path: 'data-vault/members/:memberId/pq',
              element: <PQIndexPage />,
            },
            {
              path: 'data-vault/members/:memberId/pq/sections/:sectionId',
              element: <PQSectionDetailPage />,
            },
            {
              path: 'data-vault/members/:memberId/pq/answer',
              element: <DataVaultInterviewRoute />,
            },
            {
              path: 'data-vault/members/:memberId/pq/sections/:sectionId/answer',
              element: <DataVaultInterviewRoute />,
            },
            // Data Vault History routes (doc 08)
            { path: 'data-vault/history', element: <HistoryHubPage /> },
            {
              path: 'data-vault/history/certifications/:certificationId/sections/:sectionId',
              element: <CertificationSnapshotSectionDetailPage />,
            },
            {
              path: 'data-vault/history/certifications/:certificationId',
              element: <CertificationSnapshotDetailPage />,
            },
            {
              path: 'data-vault/members/:memberId/history',
              element: <HistoryHubPage />,
            },
            {
              path: 'data-vault/members/:memberId/history/certifications/:certificationId/sections/:sectionId',
              element: <CertificationSnapshotSectionDetailPage />,
            },
            {
              path: 'data-vault/members/:memberId/history/certifications/:certificationId',
              element: <CertificationSnapshotDetailPage />,
            },
          ],
        },
        // Survey explorer (read-only, no elevation gate)
        {
          element: <SurveyLayout />,
          children: [
            { path: 'survey/:surveyId', element: <SurveyIndexPage /> },
            {
              path: 'survey/:surveyId/members/:memberId',
              element: <SurveyIndexPage />,
            },
            {
              path: 'survey/:surveyId/answer',
              element: <SurveyInterviewRoute />,
            },
            {
              path: 'survey/:surveyId/members/:memberId/answer',
              element: <SurveyInterviewRoute />,
            },
            {
              path: RENTER_PATHS.SURVEY_MY_DOCUMENTS,
              element: <MyDocumentsPage />,
            },
            {
              path: RENTER_PATHS.SURVEY_MEMBER_MY_DOCUMENTS,
              element: <MyDocumentsPage />,
            },
            {
              path: RENTER_PATHS.SURVEY_MY_DOCUMENTS_ANSWER,
              element: <MyDocumentsInterviewRoute />,
            },
            {
              path: RENTER_PATHS.SURVEY_MEMBER_MY_DOCUMENTS_ANSWER,
              element: <MyDocumentsInterviewRoute />,
            },
          ],
        },
        // AIFileUploadV2 routes (AC-2.8)
        {
          path: RENTER_PATHS.HUB,
          element: (
            <AIFileUploadV2Layout>
              <AIFileUploadV2Entry />
            </AIFileUploadV2Layout>
          ),
        },
        {
          path: RENTER_PATHS.HUB_CLASSIFY,
          element: (
            <AIFileUploadV2Layout>
              <AIFileUploadV2ClassifyFunnel />
            </AIFileUploadV2Layout>
          ),
        },
        {
          path: RENTER_PATHS.HUB_REVIEW,
          element: (
            <AIFileUploadV2Layout>
              <AIFileUploadV2InsightsReview />
            </AIFileUploadV2Layout>
          ),
        },
        {
          path: RENTER_PATHS.HUB_GROUP_EDITOR,
          element: (
            <AIFileUploadV2Layout>
              <AIFileUploadV2GroupEditor />
            </AIFileUploadV2Layout>
          ),
        },
        {
          path: RENTER_PATHS.HUB_DETAIL,
          element: (
            <AIFileUploadV2Layout>
              <AIFileUploadV2FileDetail />
            </AIFileUploadV2Layout>
          ),
        },
        // Interview (ENG-2265)
        {
          path: RENTER_PATHS.INTERVIEW,
          children: [
            { path: '', element: <InterviewRoute /> },
            { path: 'complete', element: <InterviewCompletePage /> },
            { path: 'answer', element: <InterviewReAnswerRoute /> },
            { path: 'data-transfer', element: <DataVaultTransferPage /> },
            // Proxy (on-behalf-of) DVT: carries memberId through so the page
            // can route back to the correct member's interview after commit.
            {
              path: 'data-transfer/members/:memberId',
              element: <DataVaultTransferPage />,
            },
            { path: 'members/:memberId', element: <InterviewRoute /> },
          ],
        },
      ],
    },

    {
      path: 'questionnaire/:survey_id/:section_id/:page_id',
      element: (
        <RoleBasedGuard accessibleRoles={['resident']}>
          <Questionnaire />
        </RoleBasedGuard>
      ),
    },

    // Dashboard Routes
    {
      path: 'pronto-admin',
      element: (
        <RoleBasedGuard accessibleRoles={['compliance']}>
          <DashboardLayout />
        </RoleBasedGuard>
      ),
      children: [
        { path: '', element: <Navigate to="/pronto-admin/forms" replace /> },
        // Pronto Housing Administration
        {
          path: 'forms',
          children: [
            { path: '', element: <FormList /> },
            { path: 'search/:search', element: <FormList /> },
            { path: 'new', element: <FormCreate /> },
            { path: ':id', element: <FormDetail /> },
            { path: ':id/edit', element: <FormCreate /> },
          ],
        },
        {
          path: 'questions',
          children: [
            { path: '', element: <QuestionList /> },
            { path: 'search/:search', element: <QuestionList /> },
            { path: 'new', element: <QuestionCreate /> },
            { path: ':id/edit', element: <QuestionCreate /> },
          ],
        },
        {
          path: 'certification-config',
          children: [
            {
              path: '',
              element: (
                <RoleBasedGuard
                  accessibleRoles={[DASHBOARD_GROUPS.pronto_admin]}
                >
                  <CertificationConfigTemplateList />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':certification_config_id',
              element: (
                <RoleBasedGuard
                  accessibleRoles={[DASHBOARD_GROUPS.pronto_admin]}
                >
                  <CertificationConfigTemplateWorkbench />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':certification_config_id/differences',
              element: (
                <RoleBasedGuard
                  accessibleRoles={[DASHBOARD_GROUPS.pronto_admin]}
                >
                  <CertConfigDiff />
                </RoleBasedGuard>
              ),
              children: [
                {
                  path: '',
                  element: (
                    <RoleBasedGuard
                      accessibleRoles={[DASHBOARD_GROUPS.pronto_admin]}
                    >
                      <CertsList />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: ':child_id',
                  element: (
                    <RoleBasedGuard
                      accessibleRoles={[DASHBOARD_GROUPS.pronto_admin]}
                    >
                      <DiffsDetail />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: 'templates',
          children: [{ path: '', element: <DefaultTemplates /> }],
        },
      ],
    },

    // User Management (ENG-2139 -- gated on User Administrator or Django User - Eng group, plus superusers)
    {
      path: 'user-management',
      element: (
        <UserAdministratorGuard>
          <DashboardLayout />
        </UserAdministratorGuard>
      ),
      children: [{ path: '', element: <UserManagementPage /> }],
    },

    // Compliance Routes
    { path: 'no-properties', element: <PageNoProperties /> },
    {
      path: 'workbench',
      element: (
        <RoleBasedGuard
          accessibleRoles={['compliance', ...Object.values(DASHBOARD_GROUPS)]}
        >
          <DashboardLayout />
        </RoleBasedGuard>
      ),
      children: [
        { path: '', element: <PropertyPage /> },
        { path: 'app', element: <EmptyPage /> },
        { path: 'user/settings', element: <ComplianceSettingsPage /> },
        { path: 'setup', element: <PropertiesBoard /> },
        { path: 'setup/:property_slug', element: <PropertySetup /> },
        { path: 'upload_units/:property_slug', element: <UploadUnitsWizard /> },
        {
          path: 'upload_rent_roll/:property_slug',
          element: <UploadRentRollWizard />,
        },
        { path: 'notifications', element: <NotificationsPage /> },
        {
          path: 'my-renters',
          element: (
            <RoleBasedGuard
              accessibleRoles={[DASHBOARD_GROUPS.compliance_specialist]}
            >
              <ApplicantsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'team-renters',
          element: (
            <RoleBasedGuard
              accessibleRoles={[
                DASHBOARD_GROUPS.compliance_manager,
                DASHBOARD_GROUPS.compliance_specialist,
              ]}
            >
              <RentersDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'my-work',
          element: (
            <RoleBasedGuard
              accessibleRoles={[
                DASHBOARD_GROUPS.compliance_specialist,
                DASHBOARD_GROUPS.compliance_manager,
              ]}
            >
              <MyWorkPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'my-work/:userId/:tab',
          element: (
            <RoleBasedGuard
              accessibleRoles={[
                DASHBOARD_GROUPS.compliance_specialist,
                DASHBOARD_GROUPS.compliance_manager,
              ]}
            >
              <MyWorkPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'my-work/:userId',
          element: (
            <RoleBasedGuard
              accessibleRoles={[
                DASHBOARD_GROUPS.compliance_specialist,
                DASHBOARD_GROUPS.compliance_manager,
              ]}
            >
              <MyWorkPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'workgroups',
          element: (
            <RoleBasedGuard
              accessibleRoles={[DASHBOARD_GROUPS.compliance_manager]}
            >
              <WorkgroupAdminPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'user/communications',
          element: (
            <RoleBasedGuard
              accessibleRoles={[
                DASHBOARD_GROUPS.compliance_specialist,
                DASHBOARD_GROUPS.compliance_manager,
              ]}
            >
              <UserDashboardPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'bulk_communications',
          element: (
            <RoleBasedGuard
              accessibleRoles={[
                DASHBOARD_GROUPS.compliance_specialist,
                DASHBOARD_GROUPS.compliance_manager,
                DASHBOARD_GROUPS.bulk_communications,
              ]}
            >
              <Outlet />
            </RoleBasedGuard>
          ),
          children: [
            {
              path: '',
              element: <BulkCommunicationsDashBoardPage />,
            },
            {
              path: 'create_bulk_communication',
              element: <CreateBulkCommunicationPage />,
            },
            {
              path: ':bulk_communication_id',
              element: <BulkCommunicationConfigurationPage />,
            },
          ],
        },
        {
          path: ':property_slug/certification-config/:certification_config_id',
          element: (
            <RoleBasedGuard
              accessibleRoles={[DASHBOARD_GROUPS.compliance_manager]}
            >
              <CertificationConfigWorkbench />
            </RoleBasedGuard>
          ),
        },
        {
          path: ':property_slug/certification-config',
          element: (
            <RoleBasedGuard accessibleRoles={['Certification Workbench']}>
              <CertificationConfigList />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'user/:active_tab/communications/:conversationKey',
          element: <UserDashboardPage />,
        },
        {
          path: ':property_slug/communications/:conversationKey',
          element: <PropertyPage />,
        },
        // Support Routes (placed before :property_slug catch-all so it does not match "support")
        {
          path: 'support',
          children: [
            {
              path: 'dashboard',
              element: (
                <RoleBasedGuard accessibleRoles={['Support']}>
                  <SupportDashboardPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Retired: My Support fully replaced by the Support Hub (Ask + My Activity tab).
              // Redirect old bookmarks/links straight to the hub.
              path: 'my-support',
              element: <Navigate to="/workbench/support/hub" replace />,
            },
            // ENG-4211 — one Resources destination. The R2 full-page routes still
            // exist but only as redirects: a bookmark, an email written before this
            // shipped, or a notification already in someone's bell must keep working.
            {
              path: 'cases',
              element: <Navigate to="/workbench/support/hub" replace />,
            },
            {
              path: 'cases/:caseId',
              element: <CaseDetailRedirect />,
            },
            {
              path: 'documents',
              element: (
                <Navigate to="/workbench/support/hub/documents" replace />
              ),
            },
            {
              path: 'hub',
              element: <SupportHubPage />,
            },
            {
              // One case, inside the hub. The same `CaseStream` the floating panel
              // renders, with more room; the URL is shareable and survives a reload.
              path: 'hub/cases/:caseId',
              element: <CaseDetailPage />,
            },
            {
              // §6 Option A — Documents is its own top-level tab again. The library
              // is the Assistant's, but it needs a page of its own to be searchable,
              // sortable and complete; folding it into the Assistant tab as an
              // anchor meant it could never page past the first request.
              path: 'hub/documents',
              element: <DocumentsPage />,
            },
            {
              // PA-14 — Saved prompts is a section of Settings.
              path: 'hub/prompts',
              element: <PromptsRedirect />,
            },
            {
              // PA-14 — Scheduled actions is a section of Settings. Gate lives in the
              // page: without the schedules flag the section simply is not rendered.
              path: 'hub/schedules',
              element: <SchedulesRedirect />,
            },
            {
              // PA-14 — third tab: configuration, not conversation.
              path: 'hub/settings',
              element: <SettingsPage />,
            },
            {
              // Pathless layout route: keeps the TOC sidebar mounted across
              // knowledge <-> article navigation so tree expansion state and
              // scroll position survive.
              element: <BrowseReaderLayout />,
              children: [
                {
                  // PA-14 — the Knowledge Base tab. `/hub/browse` is the pre-PA-14
                  // URL: it renders the same landing (which redirects itself when the
                  // Assistant is on) so the old Browse tab still works for users
                  // without the flag.
                  path: 'hub/knowledge',
                  element: <BrowseLanding />,
                },
                {
                  path: 'hub/browse',
                  element: <BrowseLanding />,
                },
                {
                  path: 'hub/articles/:slug',
                  element: <ArticlePage />,
                },
              ],
            },
            {
              // Old-hub only: for a user on the case surface, MY Activity is the
              // Assistant's own list, so the page redirects itself to the hub.
              path: 'hub/activity',
              element: <ActivityHubPage />,
            },
            {
              path: 'chats/:contactId',
              element: (
                <RoleBasedGuard accessibleRoles={['Support']}>
                  <ChatDetailPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'tickets/:ticketId',
              element: (
                <RoleBasedGuard accessibleRoles={['Support']}>
                  <TicketDetailPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'calls/:contactId',
              element: (
                <RoleBasedGuard accessibleRoles={['Support']}>
                  <CallDetailPage />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        { path: ':property_slug', element: <PropertyPage /> },
        // Property dashboard sub-tabs
        { path: ':property_slug/all-units', element: <PropertyPage /> },
        { path: ':property_slug/available-units', element: <PropertyPage /> },
        { path: ':property_slug/applicants', element: <PropertyPage /> },
        { path: ':property_slug/communications', element: <PropertyPage /> },
        { path: ':property_slug/log', element: <PropertyPage /> },
        { path: ':property_slug/recert_notices', element: <PropertyPage /> },
        {
          path: ':property_slug/recert_notices/:tab',
          element: <PropertyPage />,
        },
        { path: ':property_slug/vacancies', element: <PropertyPage /> },
        { path: ':property_slug/vacancy_tracker', element: <PropertyPage /> },
        {
          path: ':property_slug/vacancy_tracker/assign',
          element: <PropertyPage />,
        },
        {
          path: ':property_slug/vacancy_tracker/stacking',
          element: <PropertyPage />,
        },
        { path: ':property_slug/tasks', element: <PropertyPage /> },
        { path: ':property_slug/lottery', element: <PropertyPage /> },
        { path: ':property_slug/lottery/new', element: <PropertyPage /> },
        {
          path: ':property_slug/lottery/:batch_id',
          element: <PropertyPage />,
        },
        // ENG-4183 Campaigns. Flag-gated on the property tab; the API and the
        // WebSocket close themselves when the flag is off, so a direct URL shows
        // PageLoadError rather than a half-rendered surface.
        { path: ':property_slug/campaigns', element: <PropertyPage /> },
        { path: ':property_slug/campaigns/new', element: <PropertyPage /> },
        {
          path: ':property_slug/campaigns/:campaign_id',
          element: <PropertyPage />,
        },
        {
          // CAMP-003: resume a saved draft in the wizard.
          path: ':property_slug/campaigns/:campaign_id/edit',
          element: <PropertyPage />,
        },
        {
          path: ':property_slug/appointments',
          element: <PropertyPage />,
        },
        {
          path: ':property_slug/:certification_id',
          children: [
            {
              path: '',
              element: <Navigate to="summary" replace />,
            },
            {
              path: 'communications/:conversationKey',
              element: <CertificationPage />,
            },
            {
              path: ':tab',
              element: <CertificationPage />,
            },
          ],
        },
        // ENG-2260 Phase 4.6.E: new cert-level household routes (v2 chain, flag ON).
        // These are siblings of the :certification_id block above, NOT children of it.
        // React Router v6 gives static segments ('household') higher specificity than
        // param segments (':tab'), so /:cert_id/household always wins over /:cert_id/:tab.
        // Confirmed: no CertificationPage tab is currently named "household".
        {
          path: ':property_slug/:certification_id/household',
          element: <CertHouseholdPage />,
        },
        {
          path: ':property_slug/:certification_id/household/edit',
          element: <CertHouseholdPage edit={true} />,
        },
        {
          path: ':property_slug/household/:household_id',
          element: <HouseholdPage />,
        },
        {
          path: ':property_slug/household/:household_id/edit',
          element: <HouseholdPage edit={true} />,
        },
        { path: ':property_slug/units/:unit_id', element: <UnitPage /> },
        {
          path: ':property_slug/units/:unit_id/edit',
          element: <UnitEditPage />,
        },
        {
          path: ':property_slug/create-questionnaire',
          element: <CreateQuestionnaire />,
        },
        {
          path: ':property_slug/add-rent-roll',
          element: <EmptyPage title="Add Rent Roll Form" />,
        },
        {
          path: ':property_slug/:certification_id/notifications',
          element: <EmptyPage title="Notifications" />,
        },
      ],
    },

    // Listings Routes
    {
      path: 'listings',
      element: <ListingsLayout />,
      children: [
        { path: '', element: <ListingsDashboard /> },
        { path: 'property/:id', element: <ListingsPropertyDetail /> },
        { path: 'faqs', element: <ListingsFAQs /> },
        { path: 'property-teams', element: <ListingsPropertyTeams /> },
        { path: 'nyc', element: <ListingsDashboard /> },
      ],
    },

    {
      path: 'privacy-policy',
      element: <PrivacyPolicy />,
    },
    { path: 'cookies-policy', element: <CookiePolicy /> },
    { path: 'terms-of-use', element: <TermsOfUse /> },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '',
      element: <MainLayout />,
      children: [
        {
          path: '',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = ComponentLoader(() => import('../pages/authentication/Login'));
const ResetPassword = ComponentLoader(() =>
  import('src/pages/authentication/ResetPassword')
);
const ResetPasswordConfirmation = ComponentLoader(() =>
  import('../pages/authentication/ResetPasswordConfirmation')
);
const ActAsUser = ComponentLoader(() =>
  import('src/pages/authentication/ImpersonateUser')
);
const OneClickLogin = ComponentLoader(() =>
  import('../pages/authentication/OneClickLogin')
);
const GoogleCompletePage = ComponentLoader(() =>
  import('../pages/authentication/GoogleCompletePage')
);
const VerifyEmail = ComponentLoader(() =>
  import('../pages/authentication/VerifyEmail')
);
const SelfRegistrationLayout = ComponentLoader(() =>
  import('../layouts/SelfRegistrationLayout')
);
const SelfRegistrationListPage = ComponentLoader(() =>
  import('../pages/self-registration/SelfRegistrationListPage')
);
const SelfRegistrationPage = ComponentLoader(() =>
  import('../pages/self-registration/SelfRegistrationPage')
);
const GetStartedPage = ComponentLoader(() =>
  import('../pages/get-started/GetStartedPage')
);
const SelfRegistrationSuccessPage = ComponentLoader(() =>
  import('../pages/self-registration/SelfRegistrationSuccessPage')
);
const UlcmVerifyOtpPage = ComponentLoader(() =>
  import('../pages/Ulcm/VerifyOtpPage')
);
const UlcmCancelByTokenPage = ComponentLoader(() =>
  import('../pages/Ulcm/CancelByTokenPage')
);
const AccountRecoveryPage = ComponentLoader(() =>
  import('../pages/Ulcm/AccountRecoveryPage')
);
const ClaimPage = ComponentLoader(() => import('../pages/Claim/ClaimPage'));
// Main
const Page500 = ComponentLoader(() => import('../pages/Page500'));
const NotFound = ComponentLoader(() => import('../pages/Page404'));
const CookiePolicy = ComponentLoader(() => import('../pages/CookiePolicy'));
const PrivacyPolicy = ComponentLoader(() => import('../pages/PrivacyPolicy'));
const TermsOfUse = ComponentLoader(() => import('../pages/TermsOfUse'));

// Pronto Housing Administration

/* const ConfiguredProgramList = ComponentLoader(() => import('../pages/ProntoHousingAdministration/ConfiguredProgramList'));
 const ConfiguredProgramCreate = ComponentLoader(() => import('../pages/ProntoHousingAdministration/ConfiguredProgramCreate'));
 const ConfiguredProgramDetail = ComponentLoader(() => import('../pages/ProntoHousingAdministration/ConfiguredProgramDetail/ConfiguredProgramDetail')); */

// const QuestionMapping = ComponentLoader(lazy(() =>
// import('../components/ProntoHousingAdministration/QuestionMapping/QuestionMapping')));

// certification config
const CertificationConfigList = ComponentLoader(() =>
  import('../components/CertificationConfiguration/CertificationConfigList')
);
const CertificationConfigWorkbench = ComponentLoader(() =>
  import('../components/Compliance/Property/CertificationConfigWorkbench')
);
const CertificationConfigTemplateList = ComponentLoader(() =>
  import(
    '../pages/ProntoHousingAdministration/CertificationConfig/CertificationConfigTemplateList'
  )
);
const CertificationConfigTemplateWorkbench = ComponentLoader(() =>
  import(
    '../components/Compliance/Property/CertificationConfigTemplateWorkbench'
  )
);
const CertConfigDiff = ComponentLoader(() =>
  import('../components/Compliance/CertConfigDiff/CertConfigDiff')
);
const CertsList = ComponentLoader(() =>
  import('../components/Compliance/CertConfigDiff/CertsList')
);
const DiffsDetail = ComponentLoader(() =>
  import('../components/Compliance/CertConfigDiff/DiffsDetail')
);

const FormList = ComponentLoader(() =>
  import('../pages/ProntoHousingAdministration/FormList')
);
const FormCreate = ComponentLoader(() =>
  import('../pages/ProntoHousingAdministration/FormCreate')
);
const FormDetail = ComponentLoader(() =>
  import('../pages/ProntoHousingAdministration/FormDetail')
);

const UserManagementPage = ComponentLoader(() =>
  import('../pages/UserManagement/UserManagementPage')
);

const QuestionList = ComponentLoader(() =>
  import('../pages/ProntoHousingAdministration/QuestionList')
);
const QuestionCreate = ComponentLoader(() =>
  import('../pages/ProntoHousingAdministration/QuestionCreate')
);

const DefaultTemplates = ComponentLoader(() =>
  import('../pages/ProntoHousingAdministration/DefaultTemplates')
);

// Pronto Housing Household Members
const TenantLandingWrapper = ComponentLoader(() =>
  import('../components/TenantLandingWrapper')
);
const InviteToHouseholdHolder = ComponentLoader(() =>
  import('../pages/tenant/inviteToHousehold/InviteToHouseholdHolder')
);
const QuestionnaireCompleted = ComponentLoader(() =>
  import('../pages/tenant/Questionnaire/QuestionnaireCompleted')
);
const RenterSettingsPage = ComponentLoader(() =>
  import('../pages/tenant/RenterSettings/RenterSettingsPage')
);
const RenterNotificationsPage = ComponentLoader(() =>
  import('../pages/tenant/RenterNotificationsPage')
);
const ComplianceSettingsPage = ComponentLoader(() =>
  import('../pages/tenant/ComplianceSettings/ComplianceSettingsPage')
);

// Pronto Housing Questionnaire
const Questionnaire = ComponentLoader(() =>
  import('../pages/tenant/QuestionnaireV4/Questionnaire')
);

// Compliance Manager
const PropertyPage = ComponentLoader(() =>
  import('../pages/Compliance/PropertyPage')
);
const UnitPage = ComponentLoader(() =>
  import('../pages/Compliance/Unit/UnitPage')
);
const CreateQuestionnaire = ComponentLoader(() =>
  import('../pages/Compliance/CreateQuestionnaire')
);
const CertificationPage = ComponentLoader(() =>
  import('../pages/Compliance/CertificationPage')
);
const HouseholdPage = ComponentLoader(() =>
  import('../pages/Compliance/HouseholdPage')
);
// ENG-2260 Phase 4.6.E: cert-level household page (v2 chain).
// Full implementation is Phase 4.6.F; this import resolves the routes added in 4.6.E.
const CertHouseholdPage = ComponentLoader(() =>
  import('../pages/Compliance/CertHouseholdPage')
);
// const UnitCreate = ComponentLoader(() => import('../pages/Compliance/Unit/UnitCreate'));
const UnitEditPage = ComponentLoader(() =>
  import('../pages/Compliance/Unit/UnitEditPage')
);
const ApplicantsPage = ComponentLoader(() =>
  import('../pages/Compliance/ApplicantsPage')
);
const RentersDashboard = ComponentLoader(() =>
  import('src/components/RentersDashboard/RentersDashboard')
);
const MyWorkPage = ComponentLoader(() =>
  import('../pages/Compliance/MyWorkPage')
);
const WorkgroupAdminPage = ComponentLoader(() =>
  import('../pages/Compliance/WorkgroupAdminPage')
);

// User
const UserDashboardPage = ComponentLoader(() =>
  import('../pages/UserDashboard/UserDashboardPage')
);

const BulkCommunicationsDashBoardPage = ComponentLoader(() =>
  import('../pages/BulkCommunications/BulkCommunicationsDashboard')
);

const CreateBulkCommunicationPage = ComponentLoader(() =>
  import('../pages/BulkCommunications/CreateBulkCommunication')
);

const BulkCommunicationConfigurationPage = ComponentLoader(() =>
  import('../pages/BulkCommunications/BulkCommunicationConfiguration')
);

// Property Setup
const PropertiesBoard = ComponentLoader(() =>
  import('../pages/PropertiesBoard/PropertiesBoardPage')
);
const PropertySetup = ComponentLoader(() =>
  import('../pages/PropertySetup/PropertySetupPage')
);
const UploadUnitsWizard = ComponentLoader(() =>
  import('../pages/PropertySetup/UploadUnitsWizard')
);
const UploadRentRollWizard = ComponentLoader(() =>
  import('../pages/PropertySetup/UploadRentRollWizard')
);

const EmptyPage = ComponentLoader(() => import('../pages/EmptyPage'));
const PageNoProperties = ComponentLoader(() =>
  import('../pages/PageNoProperties')
);
const Signatures = ComponentLoader(() =>
  import('src/components/QuestionnaireSummary/RequestedSignatures/Signatures')
);
const SignaturesDetail = ComponentLoader(() =>
  import(
    'src/components/QuestionnaireSummary/RequestedSignatures/SignaturesDetail'
  )
);

// Listings
const ListingsLayout = ComponentLoader(() =>
  import('../pages/Listings/ListingsLayout')
);
const ListingsDashboard = ComponentLoader(() =>
  import('../pages/Listings/ListingsDashboard')
);
const ListingsPropertyDetail = ComponentLoader(() =>
  import('../pages/Listings/ListingsPropertyDetail')
);
const ListingsFAQs = ComponentLoader(() =>
  import('../pages/Listings/ListingsFAQs')
);
const ListingsPropertyTeams = ComponentLoader(() =>
  import('../pages/Listings/ListingsPropertyTeams')
);

// AIFileUploadV2 pages (AC-2.8)
const AIFileUploadV2Entry = ComponentLoader(() =>
  import('../components/NewRenterInterface/UploadDocuments/UploadDocumentsPage')
);
// Cert+user scoped entry — /renter/certification/:certificationId/files/:userId
function AIFileUploadV2CertEntry() {
  const { certificationId, userId } = useParams();
  return (
    <AIFileUploadV2Layout
      certificationId={certificationId ? Number(certificationId) : undefined}
      userId={userId ? Number(userId) : undefined}
      showBannerInLayout={false}
    >
      <AIFileUploadV2Entry />
    </AIFileUploadV2Layout>
  );
}
// Same but for the file detail page under the cert-scoped route
function AIFileUploadV2CertFileDetail() {
  const { certificationId, userId } = useParams();
  return (
    <AIFileUploadV2Layout
      certificationId={certificationId ? Number(certificationId) : undefined}
      userId={userId ? Number(userId) : undefined}
    >
      <AIFileUploadV2FileDetail />
    </AIFileUploadV2Layout>
  );
}
// Classify funnel under the cert-scoped route
function AIFileUploadV2CertClassify() {
  const { certificationId, userId } = useParams();
  return (
    <AIFileUploadV2Layout
      certificationId={certificationId ? Number(certificationId) : undefined}
      userId={userId ? Number(userId) : undefined}
    >
      <AIFileUploadV2ClassifyFunnel />
    </AIFileUploadV2Layout>
  );
}
// Review/summary page under the cert-scoped route
function AIFileUploadV2CertReview() {
  const { certificationId, userId } = useParams();
  return (
    <AIFileUploadV2Layout
      certificationId={certificationId ? Number(certificationId) : undefined}
      userId={userId ? Number(userId) : undefined}
    >
      <AIFileUploadV2InsightsReview />
    </AIFileUploadV2Layout>
  );
}
const AIFileUploadV2ClassifyFunnel = ComponentLoader(() =>
  import('../components/NewRenterInterface/AIFileUploadV2/ClassifyFunnel')
);
const AIFileUploadV2GroupEditor = ComponentLoader(() =>
  import(
    '../components/NewRenterInterface/AIFileUploadV2/MultiSourceGroupEditor'
  )
);

// Support
const SupportDashboardPage = ComponentLoader(() =>
  import('../pages/Support/SupportDashboardPage')
);
const ChatDetailPage = ComponentLoader(() =>
  import('../pages/Support/ChatDetailPage')
);
const TicketDetailPage = ComponentLoader(() =>
  import('../pages/Support/TicketDetailPage')
);
const CallDetailPage = ComponentLoader(() =>
  import('../pages/Support/CallDetailPage')
);
const SupportHubPage = ComponentLoader(() =>
  import('../pages/Support/SupportHubPage')
);
const CaseDetailPage = ComponentLoader(() =>
  import('../pages/Support/CaseDetailPage')
);
const SettingsPage = ComponentLoader(() =>
  import('../pages/Support/SettingsPage')
);
const DocumentsPage = ComponentLoader(() =>
  import('../pages/Support/DocumentsPage')
);
const PromptsRedirect = ComponentLoader(() =>
  import('../pages/Support/SupportRouteGates').then((m) => ({
    default: m.PromptsRedirect,
  }))
);
const SchedulesRedirect = ComponentLoader(() =>
  import('../pages/Support/SupportRouteGates').then((m) => ({
    default: m.SchedulesRedirect,
  }))
);
const BrowseReaderLayout = ComponentLoader(() =>
  import('../pages/Support/BrowseReaderLayout')
);
const BrowseLanding = ComponentLoader(() =>
  import('../pages/Support/BrowseLanding')
);
const ActivityHubPage = ComponentLoader(() =>
  import('../pages/Support/ActivityHubPage')
);
const ArticlePage = ComponentLoader(() =>
  import('../pages/Support/ArticlePage')
);
const CaseDetailRedirect = ComponentLoader(() =>
  import('../pages/Support/SupportRouteGates').then((m) => ({
    default: m.CaseDetailRedirect,
  }))
);
